import { useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import {
  Container,
  ContainerRequest,
  Piece,
  PieceAction,
  PieceLocationRequest,
  PieceType,
  Workflow,
} from '@rdTypes/apiContract';
import { getPieceTypeByWorkflow } from 'src/utils/piece';

export type LocationStateChange = {
  itemSpot?: string | number;
  storageGroupData?: ContainerRequest & Partial<Pick<Container, 'nextOrderNo'>>;
  lockerBankId?: number;
  lockerCell?: string;
}

export type LocationState = {
  itemType: PieceType;
  
} & LocationStateChange;

export type LocationLockersError = {
  lockerBankId?: string;
  lockerCell?: string;
};

export const useLocationId = (
  piece: Piece,
  pieceId: number,
  onChange: (piece: Piece) => void,
  onClose: () => void,
  context?: 'capture',
) => {
  const [spot, setSpot] = useState<LocationState>({
    itemType: getPieceTypeByWorkflow(piece.workflow),
    itemSpot: piece.pieceLocation?.packageSpot || '',
    storageGroupData: {} as ContainerRequest,
    lockerBankId: +(piece.pieceLocation?.container.lockerBankId || 0) || undefined,
    lockerCell: piece.pieceLocation?.lockerCell || undefined,
  });

  const onChangeSpot = (params: LocationStateChange) => setSpot({
    itemType: spot.itemType,
    ...params,
  });

  const [error, setError] = useState<LocationLockersError>({});

  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();

  const request: PieceLocationRequest = {};

  const onSaveLocation = async () => {
    if (piece.workflow === Workflow.Lockers) {
      if (!spot.lockerBankId || !spot.lockerCell) {
        const errors: LocationLockersError = {};
        if (!spot.lockerBankId) {
          errors.lockerBankId = 'Locker Bank is required';
        }
        if (!spot.lockerCell) {
          errors.lockerCell = 'Locker Cell is required';
        }

        setError(errors);
        return;
      }
      request.lockerBankId = spot.lockerBankId;
      request.lockerCell = spot.lockerCell;
    } else {
      if (!spot.storageGroupData) return;
      if (spot.itemType === PieceType.Package) {
        request.packageSpot = spot.itemSpot as string;
      }

      request.container = {
        id: spot.storageGroupData?.id,
        createdDate: spot.storageGroupData?.createdDate,
        containerTypeId: spot.storageGroupData?.containerTypeId,
        containerNumber: spot.storageGroupData?.containerNumber || '',
        sortOrder: spot.storageGroupData?.sortOrder,
      };
      request.orderNo = 0;
    }

    const result = await updatePieces({
      action: PieceAction.Move,
      pieces: [
        {
          id: pieceId,
          pieceLocation: request,
        },
      ],
      context,
    }).unwrap();
    onChange(result[0]);
    onClose();
  };

  return {
    error,
    spot,
    setSpot: onChangeSpot,
    onSaveLocation,
    isLoading,
    nextOrderNo: spot.storageGroupData?.nextOrderNo,
  };
};
